import UploadForm from "~components/proofOfOwnership/UploadForm"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useMutation, useQueryClient } from "react-query"
import ProofOfOwnershipService from "~services/ProofOfOwnershipService"
import imageCompression from "browser-image-compression"
import { toast } from "react-toastify"
import Modal from "~app/Modal"
import { PROOF_OF_OWNERSHIP_STATUS } from "~constants/proofOfOwnership"

export enum OPTIONS {
  PDF = "pdf",
  IMAGE = "image",
}

interface Props {
  isOpen: boolean
  userId: string
  userName?: string
  onClose: () => void
  refresh: () => Promise<void> | void
  broker?: string
}

const UploadProofOfOwnershipModal = ({
  isOpen,
  userId,
  userName,
  onClose,
  refresh,
  broker,
}: Props) => {
  const [selectedOption, setSelectedOption] = useState<OPTIONS>(OPTIONS.IMAGE)
  const queryClient = useQueryClient()
  const {
    register,
    setValue,
    getValues,
    unregister,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { name: userName ?? "" } })

  const compressImage = async (file: File): Promise<File> => {
    const options = {
      maxSizeMB: 1,
      useWebWorker: true,
      quality: 0.3,
      maxWidthOrHeight: 800,
    }
    try {
      return await imageCompression(file, options)
    } catch (error) {
      throw new Error(`Image compression failed. ${error}`)
    }
  }

  const createPdfFromImages = async files => {
    try {
      const jsPDF = (await import("jspdf")).default
      const pdf = new jsPDF()
      const pageSize = [210, 297] // A4 size in mm

      await Promise.all(
        files.map(
          (file, index) =>
            new Promise<void>(resolve => {
              const url = URL.createObjectURL(file)
              const img = new Image()
              img.src = url
              img.onload = () => {
                const imgWidth = img.width
                const imgHeight = img.height
                const ratio = Math.min(
                  pageSize[0] / imgWidth,
                  pageSize[1] / imgHeight,
                )
                const fitWidth = imgWidth * ratio
                const fitHeight = imgHeight * ratio
                const x = (pageSize[0] - fitWidth) / 2
                const y = 0 // Top of the page

                if (index > 0) pdf.addPage()
                const imgFormat = file.type.split("/")[1].toUpperCase()
                pdf.addImage(
                  img,
                  imgFormat,
                  x,
                  y,
                  fitWidth,
                  fitHeight,
                  undefined,
                  "FAST",
                )
                resolve()
              }
            }),
        ),
      )
      return pdf.output("blob")
    } catch (error) {
      toast.error("PDF creation failed")
    }
  }

  const getPdfFromImages = async formData => {
    const compressedFiles = await Promise.all(formData.image.map(compressImage))
    return await createPdfFromImages(compressedFiles)
  }

  // The whole upload runs through a mutation so `isLoading` reflects the real
  // in-flight state and keeps the Upload button disabled until it finishes —
  // otherwise a second click during the multi-step flow creates a duplicate POO.
  const { mutate: submitUpload, isLoading } = useMutation(
    async (form: any) => {
      const file =
        selectedOption === OPTIONS.IMAGE
          ? await getPdfFromImages(form)
          : form.file

      // Step 1: Create proof of ownership record
      const response = await ProofOfOwnershipService.postProofOfOwnership({
        broker: form.broker,
        name: form.name,
        sourceContentType:
          selectedOption === OPTIONS.PDF ? "application/pdf" : "image/jpeg",
        userId: userId,
      })

      if (!response?.upload_url || !response?.id) {
        throw new Error("Invalid response from server")
      }

      const pooId = response.id
      const uploadUrl = response.upload_url

      // Step 2: Upload file to S3 using presigned POST
      await ProofOfOwnershipService.uploadFile(file, uploadUrl)

      // Step 3: Update proof of ownership state
      const patchResponse = await ProofOfOwnershipService.patchProofOfOwnership(
        PROOF_OF_OWNERSHIP_STATUS.CREATED,
        pooId,
      )
      if (!patchResponse) {
        throw new Error("Failed to update proof of ownership status")
      }
    },
    {
      onSuccess: async () => {
        toast.success("Proof of ownership uploaded")
        onClose()
        // Mark every POO list/count variant stale (any user, status or sort) so
        // other screens — e.g. the create-portfolio validation queue — show the
        // new POO without waiting for staleTime to elapse.
        queryClient.invalidateQueries(["user-proofs-of-ownership"])
        queryClient.invalidateQueries(["proofs-of-ownership-counts"])
        await refresh()
      },
      onError: () => {
        toast.error("Failed to upload proof of ownership")
      },
    },
  )

  const onConfirm = handleSubmit(form => {
    // Belt-and-suspenders against a fast double-click before the button
    // re-renders into its disabled state.
    if (isLoading) return
    submitUpload(form)
  })

  return (
    <Modal
      isOpen={isOpen}
      buttonLabel={"Upload"}
      cancelLabel="Cancel"
      showOptionalButton={false}
      cancelButton={true}
      isButtonLoading={isLoading}
      onRequestClose={onClose}
      onRequestConfirm={onConfirm}
    >
      <UploadForm
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        register={register}
        setValue={setValue}
        getValues={getValues}
        unregister={unregister}
        errors={errors}
        broker={broker}
        userName={userName}
      />
    </Modal>
  )
}

export default UploadProofOfOwnershipModal
